import { useState, useEffect, useRef } from "react";
import "./AudioLevelMeter.css";
import { Typography } from "@mui/material";

const Index = () => {
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState("");
  const [currentLevel, setCurrentLevel] = useState(0);
  const [peakLevel, setPeakLevel] = useState(0);

  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const peakCanvasRef = useRef(null);
  const peakRef = useRef(0);
  const peakHoldRef = useRef(0);
  const lastPeakTime = useRef(Date.now());

  const requestMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);

      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      setHasPermission(true);
      setError("");
      startMonitoring();
    } catch (err) {
      setHasPermission(false);
      setError(
        "Microphone access denied. Please allow microphone access to use this application."
      );
      console.error("Error accessing microphone:", err);
    }
  };

  const calculateRMS = (dataArray) => {
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += (dataArray[i] / 255) ** 2;
    }
    return Math.sqrt(sum / dataArray.length);
  };

  const linearToDecibels = (linear) => {
    if (linear === 0) return -Infinity;
    return 20 * Math.log10(linear);
  };

  const drawPeakMeter = (ctx, canvas, level) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background
    ctx.fillStyle = "#1f2937";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate current peak
    peakRef.current = Math.max(level, peakRef.current * 0.95); // Smooth decay

    // Update peak hold
    if (level > peakHoldRef.current) {
      peakHoldRef.current = level;
      lastPeakTime.current = Date.now();
    } else if (Date.now() - lastPeakTime.current > 1000) {
      peakHoldRef.current *= 0.98; // Slow decay after 1 second
    }

    // Update state for display
    const currentDb = linearToDecibels(level);
    const peakDb = linearToDecibels(peakHoldRef.current);
    setCurrentLevel(currentDb);
    setPeakLevel(peakDb);

    // Draw level gradient
    const gradient = ctx.createLinearGradient(0, canvas.height, 0, 0);
    gradient.addColorStop(0, "#22c55e"); // Green
    gradient.addColorStop(0.6, "#eab308"); // Yellow
    gradient.addColorStop(0.8, "#ef4444"); // Red

    // Draw current level
    const levelHeight = canvas.height * level;
    ctx.fillStyle = gradient;
    ctx.fillRect(0, canvas.height - levelHeight, canvas.width, levelHeight);

    // Draw peak hold line
    const peakY = canvas.height - canvas.height * peakHoldRef.current;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, peakY - 2, canvas.width, 2);

    // Draw scale lines
    ctx.fillStyle = "#ffffff33";
    for (let i = 0; i <= 10; i++) {
      const y = (i / 10) * canvas.height;
      ctx.fillRect(0, y, canvas.width, 1);
    }
  };
  const draw = () => {
    if (!peakCanvasRef.current || !analyserRef.current) return;

    const peakCanvas = peakCanvasRef.current;

    const peakCtx = peakCanvas.getContext("2d");
    if (!peakCtx) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const drawVisual = () => {
      animationFrameRef.current = requestAnimationFrame(drawVisual);
      analyserRef.current?.getByteFrequencyData(dataArray);

      // Draw peak meter
      const rmsLevel = calculateRMS(dataArray);
      drawPeakMeter(peakCtx, peakCanvas, rmsLevel);
    };

    drawVisual();
  };

  const startMonitoring = () => {
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const updateLevel = () => {
      analyserRef.current.getByteFrequencyData(dataArray);

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };
    draw();
    updateLevel();
  };
  const formatDecibels = (db) => {
    if (db === -Infinity) return "-∞";
    return Math.max(db, -60).toFixed(1);
  };
  useEffect(() => {
    requestMicrophoneAccess();
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return (
    <div className="monitor-container">
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          gutterBottom
          style={{ textAlign: "center", marginBottom: "4px", marginTop: "4px" }}
        >
          Audio Level Monitor
        </Typography>
        {hasPermission ? (
          <span className="mic-status">
            <span className="recording-indicator"></span>
            Monitoring
          </span>
        ) : (
          <span className="mic-status">Microphone access required</span>
        )}
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", flex: 1 }}>
          <canvas
            ref={peakCanvasRef}
            width={48}
            height={200}
            className="peak-canvas"
          />
        </div>
        <div className="value-display">
          <div className="mt-2 text-center">
            <div className="text-cyan-400 text-sm font-mono">
              {formatDecibels(currentLevel)} dB
            </div>
            <div className="peak-value">
              Peak: {formatDecibels(peakLevel)} dB
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
